import { formatPickup, formatTime } from '../format';
import type { StockLine, StockZone } from '../stock';
import type { PdsOrder } from '../types';

// Le « Receipt » que l'agent met en page. Les montants admettent du texte : l'agent écrit tel quel ce qu'on lui donne,
// et une chaîne vide efface la colonne prix, ce qu'un bon de préparation veut (il n'a pas de prix).
type Amount = number | string;

export interface AgentReceipt {
  id?: string;
  store: { name: string; address?: string; phone?: string };
  transaction: { date: string; time: string; cashier?: string };
  items: {
    name: string;
    quantity: number;
    unitPrice: Amount;
    total: Amount;
    currency?: string;
  }[];
  summary: { subtotal: Amount; total: Amount; currency?: string };
  footer?: { message?: string };
}

// L'imprimante écrit en CP850 : les accents passent, mais ’ · × … € – et œ s'impriment en « ? ».
const PAPER_SUBSTITUTES: [RegExp, string][] = [
  [/[’‘]/g, "'"],
  [/[“”]/g, '"'],
  [/·/g, '-'],
  [/[–—]/g, '-'],
  [/×/g, 'x'],
  [/…/g, '...'],
  [/€/g, 'EUR'],
  [/œ/g, 'oe'],
  [/Œ/g, 'OE'],
  [/ | /g, ' '],
];

export const forPaper = (text: string) =>
  PAPER_SUBSTITUTES.reduce((out, [from, to]) => out.replace(from, to), text);

const day = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString('fr-BE');

const clean = <T extends AgentReceipt>(receipt: T): T => {
  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') return forPaper(value);
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object')
      return Object.fromEntries(
        Object.entries(value).map(([key, inner]) => [key, walk(inner)]),
      );
    return value;
  };
  return walk(receipt) as T;
};

const withOption = (name: string, option?: string) =>
  option ? `${name} (${option})` : name;

const pieces = (order: Pick<PdsOrder, 'items'>) =>
  order.items.reduce((sum, item) => sum + item.quantity, 0);

const orderRef = (order: Pick<PdsOrder, 'number' | 'orderNumber'>) =>
  `#${order.orderNumber ?? order.number}`;

const printedLine = (at: number, by: string) =>
  `Imprimé à ${formatTime(at)}${by ? ` par ${by}` : ''}`;

// Le bon qui reste en atelier : produits, options, note du client, heure de retrait. Sans prix.
export const orderToPrepSlip = (
  order: PdsOrder,
  now: number,
  printedBy = '',
): AgentReceipt =>
  clean({
    id: orderRef(order),
    store: {
      name: 'BON DE PRÉPARATION',
      address: `${orderRef(order)} - ${order.label}`,
      phone: `Retrait ${formatPickup(order.dueAt)}`,
    },
    transaction: { date: day(now), time: formatTime(now) },
    items: order.items.map((item) => ({
      name: withOption(item.name.toUpperCase(), item.option),
      quantity: item.quantity,
      unitPrice: '',
      total: '',
    })),
    summary: {
      subtotal: '',
      total: `${order.items.length} produits · ${pieces(order)} pièces`,
    },
    footer: {
      message: [
        order.customerNote && `Client : ${order.customerNote}`,
        printedLine(now, printedBy),
      ]
        .filter(Boolean)
        .join(' · '),
    },
  });

// Le ticket qui part avec la commande : le récapitulatif payé et le code de retrait.
export const orderToClientTicket = (
  order: PdsOrder,
  storeName: string,
  now: number,
): AgentReceipt =>
  clean({
    id: orderRef(order),
    store: {
      name: storeName || 'Nextepay',
      address: `${orderRef(order)} - ${order.label}`,
      phone: `Retrait ${formatPickup(order.dueAt)}`,
    },
    transaction: { date: day(now), time: formatTime(now) },
    items: order.items.map((item) => {
      const unit = item.unitPriceCents;
      return {
        name: withOption(item.name, item.option),
        quantity: item.quantity,
        unitPrice: unit === undefined ? '' : unit / 100,
        total: unit === undefined ? '' : (unit * item.quantity) / 100,
        // Sans prix, une devise vide empêche l'agent d'écrire « EUR » seul dans la colonne.
        currency: unit === undefined ? '' : 'EUR',
      };
    }),
    summary: {
      subtotal: order.totalPaidCents / 100,
      total: order.totalPaidCents / 100,
      currency: 'EUR',
    },
    footer: {
      message: `Code de retrait : ${order.pickupCode} - Merci et à bientôt`,
    },
  });

const stockName = (line: StockLine, done: boolean) => {
  const who = line.parts.map((part) => `#${part.ref}(${part.quantity})`);
  return withOption(
    `${done ? '[sorti] ' : ''}${line.name}`,
    line.option,
  ).concat(` - ${line.dueAt} ${who.join(' ')}`);
};

// La liste « Ce qu'il faut sortir » : une ligne par produit et heure de retrait, avec les commandes concernées.
export const stockToReceipt = (
  zones: Pick<StockZone, 'name' | 'lines'>[],
  done: StockLine[],
  storeName: string,
  stoppedAt: number,
  printedBy = '',
): AgentReceipt => {
  const todo = zones.flatMap((zone) => zone.lines);
  const total = todo.reduce((sum, line) => sum + line.quantity, 0);
  return clean({
    store: {
      name: storeName || 'Nextepay',
      address: "CE QU'IL FAUT SORTIR",
      phone: `Liste arrêtée à ${formatTime(stoppedAt)}`,
    },
    transaction: { date: day(stoppedAt), time: formatTime(stoppedAt) },
    items: [
      ...todo.map((line) => ({
        name: stockName(line, false),
        quantity: line.quantity,
        unitPrice: '',
        total: '',
      })),
      ...done.map((line) => ({
        name: stockName(line, true),
        quantity: line.quantity,
        unitPrice: '',
        total: '',
      })),
    ],
    summary: { subtotal: '', total: `${total} pièces` },
    footer: {
      message: `${printedLine(stoppedAt, printedBy)} · vérifier le tableau avant de sortir les produits`,
    },
  });
};

export const testReceipt = (label: string, now: number): AgentReceipt =>
  clean({
    store: { name: 'TEST D’IMPRESSION', address: label || undefined },
    transaction: { date: day(now), time: formatTime(now) },
    items: [{ name: 'Ticket de test', quantity: 1, unitPrice: '', total: '' }],
    summary: { subtotal: '', total: 'OK' },
    footer: { message: 'Si vous lisez ceci, l’imprimante est prête.' },
  });
